<template>
    <div class="page__content kb-overview">
        <div class="columns">
            <div class="column is-8">
                <h2 class="page__headline">{{ $t('knowledgebase.headline') }}</h2>
                <p class="page__copy">{{ $t('knowledgebase.copy') }}</p>
            </div>
            <div class="column is-4 has-text-right">
                <button
                    class="button is-primary"
                    :disabled="draftCount === 0 || publishing"
                    @click="openPublishModal"
                >
                    <span class="icon is-small">
                        <i class="fa fa-cloud-upload" />
                    </span>
                    <span>{{ publishLabel }}</span>
                </button>
            </div>
        </div>

        <div class="notification kb-overview__drafts-banner" v-if="draftCount > 0">
            {{ $tc('knowledgebase.draftsBanner', draftCount, {count: draftCount}) }}
        </div>

        <section class="kb-overview__seasons">
            <div class="kb-overview__seasons-head">
                <h3 class="kb-overview__seasons-headline">
                    {{ $t('knowledgebase.yourYear') }}
                </h3>
                <router-link
                    class="button is-small is-outlined"
                    :to="{name: 'seasons', params: {lang}}"
                >
                    <span class="icon is-small"><i class="fa fa-calendar" /></span>
                    <span>{{ $t('knowledgebase.manageSeasons') }}</span>
                </router-link>
            </div>
            <template v-if="seasons.length > 0">
                <kb-seasons-bar :seasons="seasons" />
                <p class="kb-overview__seasons-copy">
                    {{ $t('knowledgebase.seasonsBarCopy') }}
                </p>
            </template>
            <div v-else class="kb-overview__seasons-empty">
                <p class="kb-overview__seasons-empty-headline">
                    {{ $t('knowledgebase.noSeasonsHeadline') }}
                </p>
                <p class="kb-overview__seasons-empty-copy">
                    {{ $t('knowledgebase.noSeasonsCopy') }}
                </p>
            </div>
        </section>

        <section class="kb-overview__topics">
            <h3 class="kb-overview__topics-headline">{{ $t('knowledgebase.topicsHeadline') }}</h3>
            <div class="columns is-multiline">
                <div class="column is-one-third" v-for="topic in topics" :key="topic.id">
                    <div
                        class="box kb-topic-card"
                        :class="{'kb-topic-card--pending-deletion': topic.will_be_deleted}"
                        @click="openTopic(topic)"
                    >
                        <h4 class="kb-topic-card__title">
                            <kb-dot
                                v-if="topic.has_changes"
                                :title="$t('knowledgebase.unpublishedChanges')"
                            />
                            {{ topic.title || $t('knowledgebase.untitledTopic') }}
                        </h4>
                        <p class="kb-topic-card__meta">
                            <template v-if="topic.article_count === 0">
                                {{ $t('knowledgebase.noArticles') }}
                            </template>
                            <template v-else>
                                {{ $tc('knowledgebase.articleCount', topic.article_count, {count: topic.article_count}) }}
                            </template>
                            <template v-if="topic.draft_article_count > 0">
                                · <span class="has-text-warning">
                                    {{ $tc('knowledgebase.draftCount', topic.draft_article_count, {count: topic.draft_article_count}) }}
                                </span>
                            </template>
                        </p>
                        <p class="kb-topic-card__updated">
                            {{ $t('knowledgebase.updatedPrefix') }} <kb-time :value="topic.updated_at" />
                        </p>
                    </div>
                </div>
                <div class="column is-one-third">
                    <div class="box kb-topic-card kb-topic-card--add" @click="addTopic">
                        + {{ $t('knowledgebase.addTopic') }}
                    </div>
                </div>
            </div>
        </section>

        <section class="kb-overview__channels" v-if="connections.length > 0 || allChannelsCount > 0">
            <h3 class="kb-overview__topics-headline">
                {{ $t('knowledgebase.channelsHeadline') }}
                <span class="kb-overview__channels-count" v-if="connections.length > 0">
                    · {{ $tc('knowledgebase.channelCount', connections.length, {count: connections.length}) }}
                </span>
            </h3>
            <kb-channel-card
                v-for="conn in connections"
                :key="conn.id"
                :connection="conn"
                :total-published="totalPublishedArticles"
                :excluded-count="excludedCountFor(conn.id)"
                @configure="openConfigureChannel"
            />
            <button
                class="button is-small is-outlined kb-overview__add-channel"
                @click="openConnectChannel"
            >
                + {{ $t('knowledgebase.channelConnectAnother') }}
            </button>
        </section>

        <kb-prompt-modal ref="prompt" />
        <kb-publish-modal ref="publishModal" @publish="publishAll" @discard="discardAll" />
        <kb-channel-modal ref="channelModal" @changed="load" />
    </div>
</template>

<script>
import { Api } from '../../../api';
import { AppModel } from '../../../models';
import { EventBus } from '../../../utils';
import KbChannelCard from './kb-channel-card.vue';
import KbChannelModal from './kb-channel-modal.vue';
import KbDot from './kb-dot.vue';
import KbPromptModal from './kb-prompt-modal.vue';
import KbPublishModal from './kb-publish-modal.vue';
import KbSeasonsBar from './kb-seasons-bar.vue';
import KbTime from './kb-time.vue';

export default {
    name: 'kb-overview',

    components: {KbChannelCard, KbChannelModal, KbDot, KbPromptModal, KbPublishModal, KbSeasonsBar, KbTime},

    props: ['lang'],

    data() {
        return {
            topics: [],
            articles: [],
            seasons: [],
            connections: [],
            allChannelsCount: 0,
            publishing: false,
        };
    },

    computed: {
        draftCount() {
            // Mirrors the publish modal's entry list: count drafts only on
            // rows that aren't going to be deleted, and count deletions only
            // where the row itself was explicitly staged.
            let n = 0;
            for(const t of this.topics) {
                if(t.has_draft && !t.will_be_deleted) n++;
                if(t.is_pending_deletion) n++;
            }
            for(const a of this.articles) {
                if(a.title_draft != null && !a.will_be_deleted) n++;
                if(a.is_pending_deletion) n++;
                for(const v of (a.variants || [])) {
                    if(v.body_draft != null && !v.will_be_deleted) n++;
                    if(v.is_pending_deletion) n++;
                }
                if(!a.will_be_deleted) {
                    for(const ex of (a.channel_exclusions || [])) {
                        if(ex.state === 'staged_add' || ex.state === 'staged_remove') n++;
                    }
                }
            }
            return n;
        },
        publishLabel() {
            if(this.publishing) return this.$t('knowledgebase.publishing');
            if(this.draftCount === 0) return this.$t('knowledgebase.nothingToPublish');
            return this.$tc('knowledgebase.publishCount', this.draftCount, {count: this.draftCount});
        },
        totalPublishedArticles() {
            // Drafts are invisible to every channel — only count rows that
            // have been published at least once. The article serializer
            // exposes title_published as null for never-published rows.
            return this.articles.filter(a => a.title_published != null).length;
        },
    },

    methods: {
        async load() {
            const [topicsRes, articlesRes, seasonsRes] = await Promise.all([
                Api.knowledgebase.fetchTopics(),
                Api.knowledgebase.fetchArticles(),
                Api.seasons.fetchSeasons(),
            ]);
            this.topics = this._results(topicsRes);
            this.articles = this._results(articlesRes);
            this.seasons = this._results(seasonsRes).filter(s => !s.is_deleted);

            // Channel-related fetches are isolated so a 404 / 500 here can't
            // wipe the core dashboard. New backend; new endpoints; defensive.
            try {
                const connsRes = await Api.knowledgebase.fetchConnections();
                this.connections = this._results(connsRes);
            } catch (e) {
                this.connections = [];
                console.warn('Could not load channel connections', e);
            }
            try {
                const channelsRes = await Api.knowledgebase.fetchChannels();
                // Track whether channels exist at all — if the system has none,
                // we hide the "+ Connect another channel" affordance to avoid
                // leading the user into an empty modal.
                this.allChannelsCount = this._results(channelsRes).length;
            } catch (e) {
                this.allChannelsCount = 0;
                console.warn('Could not load channels', e);
            }
        },
        excludedCountFor(connectionId) {
            // The article serializer nests channel_exclusions; a LIVE row for
            // this connection counts as hidden, staged_remove also counts
            // (still hidden until publish), staged_add does *not* (article
            // is still visible until publish).
            let n = 0;
            for(const a of this.articles) {
                if(a.title_published == null) continue;
                for(const e of (a.channel_exclusions || [])) {
                    if(e.connection !== connectionId) continue;
                    if(e.state === 'live' || e.state === 'staged_remove') {
                        n++;
                        break;
                    }
                }
            }
            return n;
        },
        pendingVisibilityCountFor(connectionId) {
            // staged_add / staged_remove rows for this connection — these are
            // the per-channel changes that will apply on the next publish.
            let n = 0;
            for(const a of this.articles) {
                for(const e of (a.channel_exclusions || [])) {
                    if(e.connection !== connectionId) continue;
                    if(e.state === 'staged_add' || e.state === 'staged_remove') n++;
                }
            }
            return n;
        },
        openConnectChannel() {
            this.$refs.channelModal.openConnect({connections: this.connections});
        },
        openConfigureChannel(connection) {
            // Hand the modal a snapshot of the campground-side facts so its
            // Synthflow form can render the "what does the bot see" summary
            // without re-fetching anything.
            const excluded = this.excludedCountFor(connection.id);
            this.$refs.channelModal.openConfigure(connection, {
                totalPublished: this.totalPublishedArticles,
                excludedCount: excluded,
                visibleCount: Math.max(0, this.totalPublishedArticles - excluded),
                pendingVisibilityCount: this.pendingVisibilityCountFor(connection.id),
            });
        },
        _results(res) {
            const body = res.body;
            return body && body.results ? body.results : (body || []);
        },
        async addTopic() {
            const title = await this.$refs.prompt.prompt({
                title: this.$t('knowledgebase.addTopic'),
                placeholder: this.$t('knowledgebase.promptTopicTitle'),
                submitText: this.$t('knowledgebase.create'),
            });
            if(title == null) return;
            await Api.knowledgebase.createTopic({title_draft: title});
            await this.load();
        },
        openTopic(topic) {
            this.$router.push({name: 'kb-topic', params: {lang: this.lang, topicId: topic.id}});
        },
        openPublishModal() {
            this.$refs.publishModal.open({
                topics: this.topics,
                articles: this.articles,
                seasons: this.seasons,
                connections: this.connections,
            });
        },
        async publishAll() {
            this.publishing = true;
            try {
                await Api.knowledgebase.publishAll();
                await this.load();
            } finally {
                this.publishing = false;
            }
        },
        async discardAll() {
            this.publishing = true;
            try {
                await Api.knowledgebase.discardAll();
                await this.load();
            } finally {
                this.publishing = false;
            }
        },
        _onBootstrap() {
            // The bootstrap event fires before AppModel.activeCampground is
            // populated when the user lands on the KB route before a campground
            // is selected — guard against the resulting null-slug crash.
            if(AppModel.activeCampground) this.load();
        },
    },

    created() {
        if(AppModel.activeCampground) {
            this.load();
        }
        EventBus.$on(EventBus.BOOTSTRAP_DONE, this._onBootstrap);
    },

    beforeDestroy() {
        EventBus.$off(EventBus.BOOTSTRAP_DONE, this._onBootstrap);
    },
};
</script>

<style lang="scss" scoped>
@import '../../../variables';

.kb-overview__drafts-banner {
    background: rgba(245, 166, 35, 0.15);
    color: #8a5a00;
    border-left: 4px solid #f5a623;
}

.kb-overview__topics-headline {
    font-size: 1.1rem;
    font-weight: 600;
    margin-bottom: 12px;
}

.kb-overview__seasons {
    margin: 18px 0 28px;
}

.kb-overview__seasons-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 6px;
}

.kb-overview__seasons-headline {
    font-size: 0.85rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #666;
    margin: 0;
}

.kb-overview__seasons-copy {
    margin-top: 8px;
    font-size: 0.85rem;
    color: #888;
}

.kb-overview__seasons-empty {
    background: #fafafa;
    border: 1px dashed #ddd;
    border-radius: 6px;
    padding: 14px 16px;
}

.kb-overview__seasons-empty-headline {
    font-weight: 600;
    margin-bottom: 4px;
}

.kb-overview__seasons-empty-copy {
    color: #666;
    font-size: 0.9rem;
    margin: 0;
}

.kb-overview__channels {
    margin-top: 28px;
}

.kb-overview__channels-count {
    color: #888;
    font-weight: normal;
}

.kb-overview__add-channel {
    border-style: dashed !important;
    margin-top: 4px;
}

.kb-topic-card {
    cursor: pointer;
    transition: box-shadow 0.15s;
    min-height: 110px;

    &:hover {
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }

    &__title {
        font-size: 1.15rem;
        font-weight: 600;
        margin-bottom: 6px;
    }

    &__meta {
        font-size: 0.85rem;
        color: #666;
    }

    &__updated {
        font-size: 0.8rem;
        color: #999;
        margin-top: 4px;
    }

    &--add {
        text-align: center;
        color: #888;
        border: 2px dashed #ddd;
        background: transparent;
        box-shadow: none;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: 500;
    }

    &--pending-deletion {
        @include kb-pending-deletion('.kb-topic-card__title');
    }
}
</style>



// WEBPACK FOOTER //
// src/views/pages/knowledge-base/kb-overview.vue